import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDataProvider,
  DatePicker,
  FieldWrapperVertical,
  TimePicker,
  useUserAccess,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  Select,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import formatDateTime from '@product-framework/utils/formatDateTime';
import IconLibrary from '../IconLibrary';
import DialogCloseButton from './DialogCloseButton';

function AcceptJobDialog({ open, onClose, jobInfo, dispatchEventId }) {
  const { formatMessage } = useLocalization();

  const userAccess = useUserAccess();
  const snackbar = useSnackbar();
  const userID = userAccess?.company?.id;
  const { confirmationRef } = useDialog();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put, get } = useDataProvider(provider);
  const { id: jobId, customerServiceRequests = [], serviceJobNumber } = jobInfo;

  const countOfCSR = Array.isArray(customerServiceRequests)
    ? customerServiceRequests?.length
    : 0;

  let singleCSRButInPast = false;

  const dateOptions = customerServiceRequests?.map(date => {
    return {
      label: formatDateTime(
        date?.requestDate,
        date?.afterHour,
        date?.beforeHour,
      ),
      value: JSON.stringify(date),
    };
  });

  // If there is one date and its in the past
  if (countOfCSR === 1) {
    const now = dayjs();
    if (
      dayjs(JSON.parse(dateOptions[0].value)?.requestDate).isBefore(now, 'day')
    ) {
      singleCSRButInPast = true;
    }
  }

  let schema = null;

  if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
    schema = yup.object().shape({
      customerServiceRequests: yup.string().required(
        formatMessage({
          id: 'CSR_DATE_REQUIRED',
          defaultMessage: 'Appointment Date is required',
        }),
      ),
      appointmentDate: yup
        .string()
        .test(
          'required-if-other',
          'Date is required when "Other" is selected',
          (value, { parent }) => {
            const { customerServiceRequests: csr } = parent;

            // only validate if CSR = "other"
            if (csr === 'OTHER') {
              return value != null && value !== ''; // must have a value
            }
            return true;
          },
        )
        .test(
          'date-not-in-past',
          'Date cannot be in the past',
          (value, { parent }) => {
            const { customerServiceRequests: csr } = parent;

            // only validate if CSR = "other" AND value is provided
            if (csr === 'OTHER' && value) {
              return !dayjs(value).isBefore(dayjs(), 'day');
            }
            return true;
          },
        ),
    });
  } else if (countOfCSR === 0) {
    schema = yup.object().shape({
      appointmentDate: yup
        .string()
        .required(
          formatMessage({
            id: 'APPOINTMENT_DATE_REQUIRED',
            defaultMessage: 'Date is required',
          }),
        )
        .test(
          'is-date-in-past',
          formatMessage({
            id: 'APPOINTMENT_DATE_PAST',
            defaultMessage: 'Date cannot be in the past',
          }),
          value => {
            return !dayjs(value).isBefore(dayjs(), 'day');
          },
        ),
    });
  }

  const [loading, setLoading] = useState(false);

  const buttons = [
    {
      children: formatMessage({ id: 'ACCEPT', defaultMessage: 'Accept' }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Accept />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        onClose(null);
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const methods = useForm({
    ...(schema && { resolver: yupResolver(schema) }),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open) {
      methods.reset();
    }
  }, [open, jobId]);

  const customerServiceRequestsValue = useWatch({
    control,
    name: 'customerServiceRequests',
  });

  const getJobDispatchId = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${userID}/pending-acceptance/${jobId}`,
    );
    if (response.ok) {
      return response?.data?.dispatchEvent?.id;
    }
    return null;
  };

  const onFormSubmit = async data => {
    setLoading(true);
    const dispatchEventValue = dispatchEventId || (await getJobDispatchId());
    if (!dispatchEventValue) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ACCEPT_JOB_FAILURE',
          defaultMessage: 'Error: Could not accept the job',
        }),
      });
    } else {
      let body = {};

      if (countOfCSR === 0) {
        body = {
          serviceDate: data.appointmentDate,
          serviceDateAfterHour: data.serviceDateAfterHour,
          serviceDateBeforeHour: data.serviceDateBeforeHour,
        };
      } else if (countOfCSR === 1 && !singleCSRButInPast) {
        body = {
          serviceDate: customerServiceRequests[0].requestDate,
          serviceDateAfterHour: customerServiceRequests[0].afterHour,
          serviceDateBeforeHour: customerServiceRequests[0].beforeHour,
        };
      } else if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
        if (data?.customerServiceRequests === 'OTHER') {
          body = {
            serviceDate: data.appointmentDate,
            serviceDateAfterHour: data.serviceDateAfterHour,
            serviceDateBeforeHour: data.serviceDateBeforeHour,
          };
        } else {
          const csrDate = JSON.parse(data?.customerServiceRequests);
          body = {
            serviceDate: csrDate.requestDate,
            serviceDateAfterHour: csrDate.afterHour,
            serviceDateBeforeHour: csrDate.beforeHour,
          };
        }
      }

      const finalBody = {
        dispatchEventId: dispatchEventValue,
        ...body,
        jobRecordVersion: jobInfo.recordVersion,
      };

      const response = await put(
        `${getApiUrl()}/sxcl/v1/service-providers/${userID}/pending-acceptance/${jobId}/accept`,
        finalBody,
      );

      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was accepted successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Accepted',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    }
    setLoading(false);
  };

  const checkAndInjectDisabledForPastDates = options => {
    const now = dayjs();
    let allDisabled = true;
    const finalOptions = options.map(opt => {
      const isDisabled = dayjs(JSON.parse(opt.value)?.requestDate).isBefore(
        now,
        'day',
      );
      if (!isDisabled) {
        allDisabled = false;
      }
      return {
        ...opt,
        disabled: isDisabled,
      };
    });
    if (allDisabled) {
      finalOptions.push({
        label: 'Other',
        value: 'OTHER',
      });
    }
    return finalOptions;
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.AcceptServiceJob />
        {formatMessage({
          id: 'ACCEPT_JOB',
          defaultMessage: 'Accept Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <Box>
                {countOfCSR === 0 && (
                  <Grid2 container spacing={1}>
                    <Grid2 xs={4}>
                      <FieldWrapperVertical
                        field={{
                          name: 'appointmentDate',
                          label: {
                            localeId: 'APOINTMENT_DATE',
                            defaultMessage: 'Appointment Date',
                          },
                        }}
                        errors={errors}>
                        <DatePicker name="appointmentDate" disablePast />
                      </FieldWrapperVertical>
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateAfterHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateAfterHour',
                              label: {
                                localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                defaultMessage: 'After',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateBeforeHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateBeforeHour',
                              label: {
                                localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                defaultMessage: 'Before',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                )}
                {countOfCSR === 1 && !singleCSRButInPast && (
                  <Typography textAlign="center">
                    Do you want to accept the job for{' '}
                    {formatDateTime(
                      customerServiceRequests[0].requestDate,
                      customerServiceRequests[0].afterHour,
                      customerServiceRequests[0].beforeHour,
                    )}
                    ?
                  </Typography>
                )}
                {((countOfCSR === 1 && singleCSRButInPast) ||
                  countOfCSR > 1) && (
                  <Box>
                    <Box>
                      <Controller
                        name="customerServiceRequests"
                        control={control}
                        render={({ field }) => {
                          const { value, ...rest } = field;
                          return (
                            <FieldWrapperVertical
                              field={{
                                name: 'customerServiceRequests',
                                label: {
                                  localeId: 'APOINTMENT_DATE',
                                  defaultMessage: 'Appointment Date',
                                },
                              }}
                              errors={errors}>
                              <Select
                                {...rest}
                                options={checkAndInjectDisabledForPastDates(
                                  dateOptions,
                                )}
                              />
                            </FieldWrapperVertical>
                          );
                        }}
                      />
                    </Box>
                    {customerServiceRequestsValue === 'OTHER' && (
                      <Box sx={{ mt: 2 }}>
                        <Grid2 container spacing={1}>
                          <Grid2 xs={5}>
                            <FieldWrapperVertical
                              field={{
                                name: 'appointmentDate',
                                label: {
                                  localeId: 'APOINTMENT_DATE',
                                  defaultMessage: 'Date',
                                },
                              }}
                              errors={errors}>
                              <DatePicker name="appointmentDate" disablePast />
                            </FieldWrapperVertical>
                          </Grid2>
                          <Grid2 xs={3.5}>
                            <Controller
                              name="serviceDateAfterHour"
                              control={control}
                              render={({ field }) => (
                                <FieldWrapperVertical
                                  field={{
                                    name: 'serviceDateAfterHour',
                                    label: {
                                      localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                      defaultMessage: 'After',
                                    },
                                  }}>
                                  <TimePicker {...field} />
                                </FieldWrapperVertical>
                              )}
                            />
                          </Grid2>
                          <Grid2 xs={3.5}>
                            <Controller
                              name="serviceDateBeforeHour"
                              control={control}
                              render={({ field }) => (
                                <FieldWrapperVertical
                                  field={{
                                    name: 'serviceDateBeforeHour',
                                    label: {
                                      localeId:
                                        'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                      defaultMessage: 'Before',
                                    },
                                  }}>
                                  <TimePicker {...field} />
                                </FieldWrapperVertical>
                              )}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons.map(btn => {
              const { children, ...rest } = btn;
              return (
                <Button key={children} disabled={loading} {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default AcceptJobDialog;
